import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaMapMarkerAlt } from "react-icons/fa";
import language from "../../localization";
import { AuthContext } from "../../authContext";
import serverPath from "../../utils/serverPath";
import { getSearchTypeLabel } from "./SearchFilters";
import styles from "./style.module.css";

const safeText = (value) => String(value || "");

function Highlight({ text, query }) {
  const value = safeText(text);
  const needle = safeText(query).trim();
  if (!needle) return value;

  const lowerValue = value.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const parts = [];
  let cursor = 0;
  let index = lowerValue.indexOf(lowerNeedle);

  while (index >= 0) {
    if (index > cursor) parts.push(value.slice(cursor, index));
    parts.push(<mark key={`${index}-${parts.length}`}>{value.slice(index, index + needle.length)}</mark>);
    cursor = index + needle.length;
    index = lowerValue.indexOf(lowerNeedle, cursor);
  }

  if (cursor < value.length) parts.push(value.slice(cursor));
  return parts;
}

const localeForLanguage = (languageCode) => {
  const current = language.normalizeLanguage(languageCode);
  if (current === "ps") return "ps-AF";
  if (current === "fa") return "fa-AF";
  return "en-US";
};

const formatDate = (date, languageCode) => {
  if (!date) return "";
  try {
    return new Intl.DateTimeFormat(localeForLanguage(languageCode), {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  } catch {
    return "";
  }
};

function SearchResultItem({ result, query, selected = false, onClick }) {
  const { languageCode } = useContext(AuthContext);
  const activeLanguage = language.normalizeLanguage(languageCode || language.getLanguage());
  const isRTL = language.isRTL(activeLanguage);
  const ArrowIcon = isRTL ? FaChevronLeft : FaChevronRight;
  const date = formatDate(result.date, activeLanguage);
  const hasImage = Boolean(result.image);
  const handleClick = (event) => {
    if (!onClick) return;
    event.preventDefault();
    onClick(result);
  };

  return (
    <Link
      className={[styles.resultItem, selected ? styles.selectedResult : ""].join(" ")}
      to={result.url || "/search"}
      onClick={handleClick}
      role={onClick ? "option" : undefined}
      aria-selected={onClick ? selected : undefined}
    >
      {hasImage ? (
        <img className={styles.resultImage} src={serverPath(result.image)} alt="" loading="lazy" />
      ) : (
        <span className={styles.resultInitial} aria-hidden="true">
          {getSearchTypeLabel(result.type).charAt(0)}
        </span>
      )}
      <span className={styles.resultBody}>
        <span className={styles.resultMeta}>
          <span className={styles.typeBadge}>{getSearchTypeLabel(result.type)}</span>
          {date ? (
            <span className={styles.metaText}>
              <FaCalendarAlt aria-hidden="true" />
              {date}
            </span>
          ) : null}
          {result.location ? (
            <span className={styles.metaText}>
              <FaMapMarkerAlt aria-hidden="true" />
              {result.location}
            </span>
          ) : null}
        </span>
        <span className={styles.resultTitle}>
          <Highlight text={result.title} query={query} />
        </span>
        {result.description ? (
          <span className={styles.resultDescription}>
            <Highlight text={result.description} query={query} />
          </span>
        ) : null}
      </span>
      <span className={styles.resultArrow} aria-hidden="true">
        <ArrowIcon />
      </span>
    </Link>
  );
}

export default SearchResultItem;